import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { BoundingBoxData } from './DocumentPageRenderer';
import { DetailedEvidenceBox } from '../../types/project';

interface BoundingBoxOverlayProps {
  pageKey: string;
  documentId?: string;  // NEW: Document ID for better matching with duplicate page mappings
  boundingBoxes: BoundingBoxData;
  imageWidth: number;
  imageHeight: number;
  pdfPageWidth: number;
  pdfPageHeight: number;
  onBoxClick?: (box: number[], boxIndex: number) => void;
}

const BoundingBoxOverlay: React.FC<BoundingBoxOverlayProps> = ({
  pageKey,
  documentId,
  boundingBoxes,
  imageWidth,
  imageHeight,
  pdfPageWidth,
  pdfPageHeight,
  onBoxClick
}) => {
  // Handle nested evidence_boxes structure from backend
  let actualBoundingBoxes: BoundingBoxData = boundingBoxes || {};

  // Check if boundingBoxes has "boxes" wrapper (new structure)
  if (boundingBoxes && typeof boundingBoxes === 'object' && 'boxes' in boundingBoxes) {
    // Cast to any first to handle the type mismatch, then ensure it's BoundingBoxData format
    const boxesData = (boundingBoxes as any).boxes;
    if (boxesData && typeof boxesData === 'object' && !Array.isArray(boxesData)) {
      actualBoundingBoxes = boxesData as BoundingBoxData;
    }
  }

  // Try multiple pageKey formats to handle database inconsistencies
  // Database has both "page_10", "10", and "sub_docid_page_10" formats
  const pageNumber = pageKey.replace('page_', '');
  const possibleKeys = [
    pageKey,           // e.g., "page_10"
    pageNumber,        // e.g., "10"
    `${pageNumber}`,   // ensure string format
  ];

  // ENHANCED: Prioritize current documentId if provided (handles duplicate page mappings)
  if (documentId && actualBoundingBoxes && typeof actualBoundingBoxes === 'object') {
    // Try exact match first: sub_docid_page_10
    const exactDocKey = `${documentId}_page_${pageNumber}`;
    if (Object.keys(actualBoundingBoxes).includes(exactDocKey)) {
      possibleKeys.unshift(exactDocKey);  // Add to front of list for priority
    }
  }

  // Also try ALL document-prefixed keys (new format: sub_docid_page_10)
  // This handles cases where evidence boxes are stored under a different document ID
  // due to duplicate page mappings
  if (actualBoundingBoxes && typeof actualBoundingBoxes === 'object') {
    const documentPrefixedKeys = Object.keys(actualBoundingBoxes).filter(key =>
      key.includes(`page_${pageNumber}`) || key.includes(`_${pageNumber}`)
    );
    possibleKeys.push(...documentPrefixedKeys);
  }

  let pageBoxes = null;
  let usedKey = '';

  // Find the first matching key
  for (const key of possibleKeys) {
    if (actualBoundingBoxes && actualBoundingBoxes[key] && Array.isArray(actualBoundingBoxes[key])) {
      pageBoxes = actualBoundingBoxes[key];
      usedKey = key;
      break;
    }
  }

  // Debug logging to help diagnose issues
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 BoundingBoxOverlay debug:', {
      currentDocumentId: documentId,
      pageKey,
      pageNumber,
      possibleKeys,
      usedKey,
      originalBoundingBoxesKeys: boundingBoxes ? Object.keys(boundingBoxes) : [],
      actualBoundingBoxesKeys: actualBoundingBoxes ? Object.keys(actualBoundingBoxes) : [],
      hasBoxesWrapper: boundingBoxes && 'boxes' in boundingBoxes,
      foundBoxes: pageBoxes?.length || 0,
      matchingProcess: `Checked ${possibleKeys.length} possible keys, found match: ${usedKey || 'none'}`,
      dataQualityNote: usedKey && !usedKey.includes(documentId || '') ? '⚠️ Using fallback: boxes from different document (duplicate page mapping issue)' : usedKey ? '✅ Found boxes for current document' : '❌ No boxes found'
    });
  }

  if (!pageBoxes || !Array.isArray(pageBoxes) || pageBoxes.length === 0) {
    return null;
  }

  // Handle both formats: legacy number[][] and new DetailedEvidenceBox[]
  let normalizedBoxes: number[][] = [];

  // Check if pageBoxes contains DetailedEvidenceBox objects
  if (pageBoxes.length > 0 && typeof pageBoxes[0] === 'object' && 'coordinates' in pageBoxes[0]) {
    // New format: DetailedEvidenceBox[]
    normalizedBoxes = (pageBoxes as DetailedEvidenceBox[]).map(box => box.coordinates);
  } else if (pageBoxes.length === 4 && typeof pageBoxes[0] === 'number') {
    // Legacy single box format: [x1,y1,x2,y2]
    normalizedBoxes = [pageBoxes as unknown as number[]];
  } else {
    // Legacy array of boxes format: [[x1,y1,x2,y2], [x1,y1,x2,y2]]
    normalizedBoxes = pageBoxes as number[][];
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    >
      {normalizedBoxes.map((box, index) => {
        // Validate box format: [x1, y1, x2, y2]
        if (!Array.isArray(box) || box.length !== 4) {
          console.warn(`Invalid bounding box format on ${pageKey}:`, box);
          return null;
        }

        // CRITICAL: AI provides coordinates in [ymin, xmin, ymax, xmax] format
        const [ymin, xmin, ymax, xmax] = box;

        // Validate coordinates are numbers
        if ([ymin, xmin, ymax, xmax].some(coord => typeof coord !== 'number' || isNaN(coord))) {
          console.warn(`Non-numeric coordinates in ${pageKey}:`, box);
          return null;
        }

        // NEW APPROACH: AI now provides normalized coordinates (0-1000 integer)
        // Check if coordinates are normalized (0-1000) or legacy format
        const isNormalized1000 = ymin <= 1000 && xmin <= 1000 && ymax <= 1000 && xmax <= 1000 &&
                                 Number.isInteger(ymin) && Number.isInteger(xmin) && Number.isInteger(ymax) && Number.isInteger(xmax);
        const isNormalizedDecimal = ymin <= 1.0 && xmin <= 1.0 && ymax <= 1.0 && xmax <= 1.0;

        let leftPercent: number, topPercent: number, widthPercent: number, heightPercent: number;
        let pdfX1: number, pdfY1: number, pdfX2: number, pdfY2: number;

        if (isNormalized1000) {
          // NEW: 0-1000 coordinate system (current standard)
          // CRITICAL: Coordinates are [ymin, xmin, ymax, xmax] - convert properly
          console.log(`🔍 COORDINATE DEBUG - ${pageKey} Box #${index}:`, {
            originalCoords: [ymin, xmin, ymax, xmax],
            pdfPageSize: `${pdfPageWidth}x${pdfPageHeight}`,
            imageSize: `${imageWidth}x${imageHeight}`,
            step: 'normalize_to_pdf'
          });

          // Convert 0-1000 to PDF coordinates
          pdfX1 = (xmin / 1000) * pdfPageWidth;   // xmin -> left edge
          pdfY1 = (ymin / 1000) * pdfPageHeight;  // ymin -> top edge
          pdfX2 = (xmax / 1000) * pdfPageWidth;   // xmax -> right edge
          pdfY2 = (ymax / 1000) * pdfPageHeight;  // ymax -> bottom edge

          console.log(`📐 PDF Coordinates:`, {
            pdfCoords: [pdfX1, pdfY1, pdfX2, pdfY2],
            conversion: `${xmin}/1000 * ${pdfPageWidth} = ${pdfX1}`,
            step: 'pdf_coordinates'
          });

          // Then convert PDF coordinates to display coordinates
          const left = Math.min(pdfX1, pdfX2);
          const top = Math.min(pdfY1, pdfY2);
          const width = Math.abs(pdfX2 - pdfX1);
          const height = Math.abs(pdfY2 - pdfY1);

          // Scale PDF coordinates to display image coordinates
          const displayScale = imageWidth / pdfPageWidth;
          const leftPixels = left * displayScale;
          const topPixels = top * displayScale;
          const widthPixels = width * displayScale;
          const heightPixels = height * displayScale;

          // Convert to percentages for overlay
          leftPercent = (leftPixels / imageWidth) * 100;
          topPercent = (topPixels / imageHeight) * 100;
          widthPercent = (widthPixels / imageWidth) * 100;
          heightPercent = (heightPixels / imageHeight) * 100;

        } else if (isNormalizedDecimal) {
          // LEGACY: 0.0-1.0 decimal coordinates (backward compatibility)
          // Note: Legacy format may be [x1, y1, x2, y2] - treating as such for backward compatibility
          pdfX1 = ymin * pdfPageWidth;  // May actually be x1 in legacy format
          pdfY1 = xmin * pdfPageHeight; // May actually be y1 in legacy format
          pdfX2 = ymax * pdfPageWidth;  // May actually be x2 in legacy format
          pdfY2 = xmax * pdfPageHeight; // May actually be y2 in legacy format

          // Then convert PDF coordinates to display coordinates (same as legacy method)
          const left = Math.min(pdfX1, pdfX2);
          const top = Math.min(pdfY1, pdfY2);
          const width = Math.abs(pdfX2 - pdfX1);
          const height = Math.abs(pdfY2 - pdfY1);

          // Scale PDF coordinates to display image coordinates
          const displayScale = imageWidth / pdfPageWidth;
          const leftPixels = left * displayScale;
          const topPixels = top * displayScale;
          const widthPixels = width * displayScale;
          const heightPixels = height * displayScale;

          // Convert to percentages for overlay
          leftPercent = (leftPixels / imageWidth) * 100;
          topPercent = (topPixels / imageHeight) * 100;
          widthPercent = (widthPixels / imageWidth) * 100;
          heightPercent = (heightPixels / imageHeight) * 100;

        } else {
          // LEGACY: Handle old PDF coordinate format for backward compatibility
          // Legacy format could be either [x1,y1,x2,y2] or [ymin,xmin,ymax,xmax]
          // Assume [x1,y1,x2,y2] for very old legacy data
          const left = Math.min(ymin, ymax);   // treating first coord as x
          const top = Math.min(xmin, xmax);    // treating second coord as y
          const width = Math.abs(ymax - ymin); // width
          const height = Math.abs(xmax - xmin); // height

          // Convert PDF coordinates to display percentages
          const displayScale = imageWidth / pdfPageWidth;
          const leftPixels = left * displayScale;
          const topPixels = top * displayScale;
          const widthPixels = width * displayScale;
          const heightPixels = height * displayScale;

          leftPercent = (leftPixels / imageWidth) * 100;
          topPercent = (topPixels / imageHeight) * 100;
          widthPercent = (widthPixels / imageWidth) * 100;
          heightPercent = (heightPixels / imageHeight) * 100;

          // Use original coordinates for backend (in original order)
          pdfX1 = ymin; // treating as x1 in legacy
          pdfY1 = xmin; // treating as y1 in legacy
          pdfX2 = ymax; // treating as x2 in legacy
          pdfY2 = xmax; // treating as y2 in legacy
        }

        const detectedFormat = isNormalized1000 ? 'NORMALIZED (0-1000)' :
                               isNormalizedDecimal ? 'NORMALIZED (0.0-1.0)' : 'LEGACY (pixel/PDF)';

        console.log('🎯 COORDINATE SYSTEM DETECTION:', {
          originalAiCoordinates: box,
          parsedAs: `[ymin=${ymin}, xmin=${xmin}, ymax=${ymax}, xmax=${xmax}]`,
          detectedFormat,
          pdfPageSize: `${pdfPageWidth}x${pdfPageHeight}`,
          displayImageSize: `${imageWidth}x${imageHeight}`,
          conversion: {
            normalizedCoords: isNormalized1000 || isNormalizedDecimal ? [ymin, xmin, ymax, xmax] : 'N/A',
            pdfCoords: [pdfX1, pdfY1, pdfX2, pdfY2],
            displayPercentages: { leftPercent, topPercent, widthPercent, heightPercent }
          }
        });

        // Skip boxes with invalid dimensions
        if (widthPercent <= 0 || heightPercent <= 0) {
          return null;
        }

        return (
          <Tooltip
            key={`${pageKey}-box-${index}`}
            title={`Click to expand evidence area ${index + 1}`}
            arrow
            placement="top"
          >
            <Box
              onClick={() => {
                // Create PDF coordinate array for backend
                const pdfCoordinates = [pdfX1, pdfY1, pdfX2, pdfY2];

                console.log('🎯 COORDINATE CLICK:', {
                  originalAiBox: box,
                  detectedFormat,
                  sentToBackend: pdfCoordinates,
                  pageKey,
                  pdfPageSize: `${pdfPageWidth}x${pdfPageHeight}`,
                  displayImageSize: `${imageWidth}x${imageHeight}`,
                  overlayPercentages: {
                    leftPercent,
                    topPercent,
                    widthPercent,
                    heightPercent
                  },
                  coordinateFlow: isNormalized1000 ? '0-1000 → PDF → Display' :
                                 isNormalizedDecimal ? '0.0-1.0 → PDF → Display' :
                                 'Legacy → Display scale → PDF'
                });
                onBoxClick && onBoxClick(pdfCoordinates, index + 1);
              }}
              sx={{
                position: 'absolute',
                left: `${leftPercent}%`,
                top: `${topPercent}%`,
                width: `${widthPercent}%`,
                height: `${heightPercent}%`,
                border: 3,
                borderColor: 'error.main',  // Use red for high visibility during debugging
                backgroundColor: 'error.main',
                opacity: 0.4,  // Increased opacity for debugging
                borderRadius: 1,
                pointerEvents: 'auto',
                cursor: 'pointer',  // Changed to pointer for clickability
                transition: 'all 0.2s ease-in-out',
                boxShadow: 2,  // Add shadow for visibility
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',  // Slight scale on hover for feedback
                  zIndex: 10,
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
                // Debug info overlay
                '&::after': {
                  content: `"🔍 ${index + 1}"`,  // Added magnifying glass to indicate clickability
                  position: 'absolute',
                  top: '2px',
                  left: '2px',
                  backgroundColor: 'error.main',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  lineHeight: 1,
                  pointerEvents: 'none',  // Don't interfere with click
                }
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default BoundingBoxOverlay;